import { useState, useMemo } from 'react';
import { useUrlState } from '@/hooks/useUrlState';
import {
  Box,
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  Tooltip,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Modal } from '@liminal/web-ui/overlays/Modal';
import { styled } from '@mui/material/styles';
import { useNimMemories } from '@/hooks/useNimMemories';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useQuery } from '@tanstack/react-query';
import { adminClient } from '@/lib/adminClient';
import { formatOrderDate } from '@liminal/utils/date';
import { truncateMiddle } from '@liminal/utils/truncate';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SearchInput } from '@liminal/web-ui/forms/SearchInput';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { UserIdLink } from '@/components/ui/UserIdLink';
import type { MemoryEntry } from '@/api/types';
import { fontSizes } from '@liminal/brand/scale';
import { FONT_STACK_BODY } from '@liminal/brand/font-faces';

const PAGE_SIZE = 50;

// The memory types nim writes (product/nim/memory/memory_service). '' is "any" and is dropped before
// the request is built.
const ContentCell = styled(Typography)({
  maxWidth: 360,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

type OperationVariant = 'success' | 'info' | 'error';

const operationVariants: Record<string, OperationVariant> = {
  ADD: 'success',
  UPDATE: 'info',
  DELETE: 'error',
};

/**
 * nim writes the extractor's confidence into the summary text as a trailing
 * "(87%)" rather than as a field, so the Content and Confidence columns are
 * both readings of one string. They live here so the cell and the sort cannot
 * disagree about where the split is.
 */
function memoryContent(memory: MemoryEntry): string {
  return memory.contentSummary.replace(/\s*\(\d+%\)\s*$/, '');
}

function memoryConfidence(memory: MemoryEntry): number | null {
  const match = memory.contentSummary.match(/\((\d+)%\)\s*$/);
  return match?.[1] === undefined ? null : Number(match[1]);
}

function useOwnerEmails(ownerIds: string[]) {
  const uniqueIds = useMemo(() => [...new Set(ownerIds.filter(Boolean))], [ownerIds]);

  const queries = useQuery({
    queryKey: ['ownerEmails', uniqueIds],
    queryFn: async () => {
      const emailMap: Record<string, string> = {};
      await Promise.allSettled(
        uniqueIds.map(async (id) => {
          try {
            const profile = await adminClient.getUser(id);
            emailMap[id] = profile.email ?? id;
          } catch {
            emailMap[id] = id;
          }
        })
      );
      return emailMap;
    },
    enabled: uniqueIds.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  return queries;
}

const FILTER_DEFAULTS = { owner: '' };

export function NimMemoryPage() {
  const [{ owner: ownerFilter }, setParams] = useUrlState(FILTER_DEFAULTS);
  const [expandedContent, setExpandedContent] = useState<string | null>(null);

  const pagination = useCursorPagination(PAGE_SIZE);

  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const ownerIsId = isUUID.test(ownerFilter);

  const { data, isLoading, isError } = useNimMemories({
    pageSize: PAGE_SIZE,
    pageToken: pagination.pageToken,
    ownerIdFilter: ownerIsId ? ownerFilter : undefined,
  });

  // See FFlagOverridesPage: `?? []` is a fresh array each render.
  const allMemories: MemoryEntry[] = useMemo(() => data?.memories ?? [], [data]);
  const totalCount = data?.totalCount ?? 0;
  const nextPageToken = data?.nextPageToken ?? '';

  const ownerIds = useMemo(() => allMemories.map((m) => m.ownerId), [allMemories]);
  const { data: emailMap } = useOwnerEmails(ownerIds);

  const memories = useMemo(() => {
    if (!ownerFilter || ownerIsId || !emailMap) return allMemories;
    const q = ownerFilter.toLowerCase();
    return allMemories.filter((m) => {
      const email = emailMap[m.ownerId];
      return email?.toLowerCase().includes(q);
    });
  }, [allMemories, ownerFilter, ownerIsId, emailMap]);

  const columnCount = 7;

  // Owner renders as an email this page resolves separately, and confidence is
  // read back out of the summary text, so the accessors are built here rather
  // than at module scope.
  const sortAccessors = useMemo(
    () => ({
      content: (memory: MemoryEntry) => memoryContent(memory),
      confidence: (memory: MemoryEntry) => memoryConfidence(memory),
      owner: (memory: MemoryEntry) => emailMap?.[memory.ownerId] ?? memory.ownerId,
      operation: (memory: MemoryEntry) => memory.operation,
      conversationId: (memory: MemoryEntry) => memory.conversationId,
      createdAt: (memory: MemoryEntry) => memory.createdAt,
      updatedAt: (memory: MemoryEntry) => memory.updatedAt,
    }),
    [emailMap],
  );

  // Newest first, matching the order nim returns. Sorts the page on screen —
  // the endpoint pages by cursor and takes no sort parameter.
  const { rows: sortedMemories, sort } = useTableSort(
    memories,
    sortAccessors,
    'createdAt',
    'desc',
  );

  const handleNextPage = () => pagination.goToNextPage(nextPageToken);
  const handlePrevPage = () => pagination.goToPreviousPage();
  const resetPagination = () => pagination.reset();

  return (
    <Stack spacing={3}>
      {/* Filters */}
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <SearchInput
          value={ownerFilter}
          onChange={(value) => {
            setParams({ owner: value });
            resetPagination();
          }}
          placeholder="Filter by email or owner ID..."
          width={340}
        />
        {totalCount > 0 && (
          <Typography variant="body2" color="text.secondary">
            {totalCount.toLocaleString()} memories total
          </Typography>
        )}
      </Stack>

      {/* Table */}
      <Card variant="elevated" padding="none">
        <TableContainer>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <SortableTableCell column="content" sort={sort}>
                  Content
                </SortableTableCell>
                <SortableTableCell column="confidence" sort={sort}>
                  Confidence
                </SortableTableCell>
                <SortableTableCell column="owner" sort={sort}>
                  Owner
                </SortableTableCell>
                <SortableTableCell column="operation" sort={sort}>
                  Operation
                </SortableTableCell>
                <SortableTableCell column="conversationId" sort={sort}>
                  Conversation
                </SortableTableCell>
                <SortableTableCell column="createdAt" sort={sort}>
                  Created
                </SortableTableCell>
                <SortableTableCell column="updatedAt" sort={sort}>
                  Updated
                </SortableTableCell>
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={columnCount}
              isLoading={isLoading}
              isError={isError}
              isEmpty={sortedMemories.length === 0}
              loadingRows={10}
              emptyMessage="No memories found."
              errorMessage="Could not load memories."
            >
              {sortedMemories.map((memory) => (
                      <TableRow
                        key={memory.id}
                        sx={{
                          opacity: memory.deletedAt ? 0.5 : 1,
                        }}
                      >
                        <TableCell>
                          <ContentCell
                            variant="body2"
                            onClick={() => setExpandedContent(memoryContent(memory))}
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {memoryContent(memory)}
                          </ContentCell>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                            {memoryConfidence(memory) === null
                              ? '\u2014'
                              : `${memoryConfidence(memory)}%`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {memory.ownerId ? (
                            <Tooltip title={memory.ownerId} arrow>
                              <Box component="span">
                                <UserIdLink userId={memory.ownerId}>
                                  {emailMap?.[memory.ownerId] ?? truncateMiddle(memory.ownerId)}
                                </UserIdLink>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" sx={{ fontFamily: FONT_STACK_BODY, fontSize: fontSizes.xs }}>
                              {'\u2014'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusPill
                            label={memory.operation}
                            tone={operationVariants[memory.operation] ?? 'neutral'}
                            size="sm"
                          />
                        </TableCell>
                        <TableCell>
                          {memory.conversationId ? (
                            <Tooltip title={memory.conversationId} arrow>
                              <Typography variant="body2" sx={{ fontFamily: FONT_STACK_BODY, fontSize: fontSizes.xs }}>
                                {truncateMiddle(memory.conversationId)}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.disabled">{'\u2014'}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                            {formatOrderDate(memory.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                            {formatOrderDate(memory.updatedAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
              ))}
            </DataTableBody>
          </Table>
        </TableContainer>
      </Card>

      <CursorPagination
        firstRowNumber={pagination.firstRowNumber}
        count={memories.length}
        hasPreviousPage={pagination.hasPreviousPage}
        hasNextPage={!!nextPageToken}
        onPreviousPage={handlePrevPage}
        onNextPage={handleNextPage}
        disabled={isLoading}
      />
      {/* Content detail dialog */}
      <Modal
        open={expandedContent !== null}
        title="Memory Content"
        onClose={() => setExpandedContent(null)}
        width="md"
      >
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {expandedContent}
        </Typography>
      </Modal>
    </Stack>
  );
}
